import React from "react";
import { useEffect } from "react";
import * as yup from "yup";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import Swal from "sweetalert2";
import {
  ISpecialite,
  SpecialiteFormData,
} from "../../../../utils/api/specialite/specialite.type";
import { useAppSelector } from "../../../../redux/hook";
import { useAddOrUpdateSpecialiteMutation, useDeleteSpecialiteMutation } from "../../../../utils/api/specialite/specialite.api";
import { cleannerError, closeModal } from "../../../../utils/Utils";
import { Color } from "../../../../utils/theme";

function UseAddOrEditSpecialiteForm(modalId: string, item?: ISpecialite) {
  const validationSchema = yup.object().shape({
    nom: yup.string().required().label("Le nom"),
  });
  const {
    register,
    handleSubmit,
    formState: { errors },
    clearErrors,
    setValue,
    reset,
  } = useForm<SpecialiteFormData>({
    resolver: yupResolver(validationSchema),
  });
  const { user } = useAppSelector((s) => s?.user);
  const [sendData, { isLoading }] = useAddOrUpdateSpecialiteMutation();

  useEffect(() => {
    cleannerError(errors, clearErrors);
  }, [errors]);

  useEffect(() => {
    if (item?.id) {
      setValue("nom", item?.nom);
    }
  }, [item]);

  const handleReset = () => {
    closeModal(modalId);
    if (!item) {
      reset();
    }
  }

  const onSubmit = async (data: SpecialiteFormData) => {
    // console.log({ data });
    const res = await sendData({ slug: item?.slug as string, data });
    if ("data" in res) {
      Swal.fire({
        icon: "success",
        title: `Specialité ${item ? "modifiée" : "ajoutée"} avec succès`,
        showConfirmButton: false,
        timer: 2000,
        iconColor: Color.success,
      }).then(() => {
        handleReset()
      });
    } else if ("error" in res) {
      const err = res.error as any;
      Swal.fire({
        icon: "error",
        title: err.data?.message ?? "Une erreur inconnue a survenu",
        showConfirmButton: false,
        timer: 5000,
      });
    }
  };

  return {
    register,
    errors: errors,
    onSubmit: handleSubmit(onSubmit),
    isLoading,
    handleReset
  };
}

export default UseAddOrEditSpecialiteForm;

export function UseDeleteSpecialite(slug: string) {
  const [sendData] = useDeleteSpecialiteMutation();
  const onDelete = async () => {
    await Swal.fire({
      title: `Êtes-vous sûr de vouloir supprimer cette spécialité ?`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "OUI",
      cancelButtonText: "NON",
      showLoaderOnConfirm: true,
      iconColor: Color.themeColor,
      confirmButtonColor: Color.themeColor,
      preConfirm: () => {
        return sendData(slug);
      },
      allowOutsideClick: () => !Swal.isLoading(),
    }).then((result: any) => {
      if (result?.isConfirmed) {
        if (result?.value && !result?.value?.error) {
          Swal.fire({
            icon: "success",
            title: `La spécialité a été supprimée avec succès!`,
            iconColor: Color.themeColor,
            showConfirmButton: false,
            timer: 1500,
          });
        } else {
          let err = result?.value?.error;
          Swal.fire({
            icon: "error",
            title: err?.data?.message
              ? err?.data?.message
              : `Une erreur de statut ${err?.status} est survenue!`,
            showConfirmButton: false,
            timer: 5000,
          });
        }
      }
    });
  };
  return onDelete;
}